import { Component } from '@theme/component';
import { ThemeEvents } from '@theme/events';
import { StandardEvents } from '@shopify/events';

export class MediaGallery extends Component {
  connectedCallback() {
    super.connectedCallback();

    const { signal } = this.#controller;
    const target = this.closest('.shopify-section, dialog');

    target?.addEventListener(StandardEvents.productSelect, this.#handleProductSelect, { signal });
    this.refs.zoomDialogComponent?.addEventListener(ThemeEvents.zoomMediaSelected, this.#handleZoomMediaSelected, {
      signal,
    });

    this.#setupDaymeritGallery(signal);
  }

  #controller = new AbortController();
  #daymeritGalleryRoot = null;
  #daymeritSlideObserver = null;

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#daymeritSlideObserver?.disconnect();
    this.#daymeritSlideObserver = null;
    this.#controller.abort();
  }

  #handleProductSelect = (event) => {
    if (!(event.target instanceof Element) || event.target.closest('product-card')) return;

    event.promise
      .then(({ detail }) => {
        if (!detail?.html) return;
        const { html } = detail;

        // DAYMERIT keeps its custom thumbnail strip outside <media-gallery>.
        // Replace the whole custom gallery shell so variant changes cannot leave
        // a new main gallery paired with stale thumbnails from the previous finish.
        const currentDaymeritRoot = this.closest('[data-dm-product-gallery]');
        if (currentDaymeritRoot instanceof HTMLElement) {
          const rootId = currentDaymeritRoot.dataset.dmProductGallery;
          const newDaymeritRoot = rootId
            ? html.querySelector(`[data-dm-product-gallery="${rootId}"]`)
            : null;

          if (newDaymeritRoot) {
            currentDaymeritRoot.replaceWith(newDaymeritRoot);
            return;
          }
        }

        const newMediaGallery = html.querySelector('media-gallery');
        if (!newMediaGallery) return;
        this.replaceWith(newMediaGallery);
      })
      .catch((error) => {
        if (error?.name !== 'AbortError') console.warn('[media-gallery] Event promise rejected:', error);
      });
  };

  #handleZoomMediaSelected = async (event) => {
    this.slideshow?.select(event.detail.index, undefined, { animate: false });
  };

  #setupDaymeritGallery(signal) {
    const root = this.closest('[data-dm-product-gallery]');
    if (!(root instanceof HTMLElement)) return;

    this.#daymeritGalleryRoot = root;
    this.#disableDaymeritSticky();
    this.#patchDaymeritThumbnailScrolling();

    // Capture before the legacy inline handler. We deliberately bypass the
    // Horizon slideshow .select() method here: the custom thumbs move the real
    // carousel scroller directly, which avoids initialization and re-render races.
    root.addEventListener('click', this.#handleDaymeritThumbnailClick, { capture: true, signal });

    this.#daymeritSlideObserver = new MutationObserver(() => this.#syncDaymeritThumbnails());
    for (const slide of this.querySelectorAll('slideshow-slide')) {
      this.#daymeritSlideObserver.observe(slide, {
        attributes: true,
        attributeFilter: ['aria-hidden'],
      });
    }

    queueMicrotask(() => this.#syncDaymeritThumbnails());
  }

  #disableDaymeritSticky() {
    // The stock gallery ships with .sticky-content. For this one-main-image +
    // thumbnail layout it makes the large image follow the page and overlap the
    // thumbnail strip while scrolling. Keep the entire media column in normal flow.
    this.classList.remove('sticky-content');
    this.style.setProperty('position', 'static', 'important');
    this.style.setProperty('top', 'auto', 'important');
    this.style.setProperty('inset-block-start', 'auto', 'important');
    this.style.setProperty('align-self', 'start', 'important');
    this.style.setProperty('z-index', 'auto', 'important');
  }

  #patchDaymeritThumbnailScrolling() {
    const root = this.#daymeritGalleryRoot;
    if (!root) return;

    for (const button of root.querySelectorAll('[data-dm-gallery-thumb]')) {
      if (!(button instanceof HTMLElement) || button.dataset.dmScrollPatched === 'true') continue;
      button.dataset.dmScrollPatched = 'true';

      // The legacy custom script calls scrollIntoView(). Keep that scrolling
      // inside the thumbnail strip, horizontally or vertically, without moving the page.
      button.scrollIntoView = (options = {}) => {
        this.#scrollDaymeritThumbnail(button, options);
      };
    }
  }

  #scrollDaymeritThumbnail(button, options = {}) {
    const strip = button.closest('.dm-product-gallery__thumbnails');
    if (!(strip instanceof HTMLElement)) return;

    const behavior = typeof options === 'object' && options?.behavior === 'smooth' ? 'smooth' : 'auto';
    const isVertical = getComputedStyle(strip).flexDirection === 'column';

    if (isVertical) {
      const targetTop = button.offsetTop - (strip.clientHeight - button.offsetHeight) / 2;
      strip.scrollTo({ top: Math.max(0, targetTop), behavior });
      return;
    }

    const targetLeft = button.offsetLeft - (strip.clientWidth - button.offsetWidth) / 2;
    strip.scrollTo({ left: Math.max(0, targetLeft), behavior });
  }

  #handleDaymeritThumbnailClick = (event) => {
    if (!(event.target instanceof Element)) return;

    const root = this.#daymeritGalleryRoot;
    const button = event.target.closest('[data-dm-gallery-thumb]');
    if (!root || !(button instanceof HTMLElement) || !root.contains(button)) return;

    const index = Number(button.dataset.dmGalleryThumb);
    if (!Number.isInteger(index)) return;

    event.preventDefault();
    event.stopImmediatePropagation();

    const slideshow = this.slideshow;
    const scroller = slideshow?.querySelector('slideshow-slides');
    if (!(scroller instanceof HTMLElement)) return;

    const slides = Array.from(scroller.children).filter(
      (element) => element instanceof HTMLElement && element.matches('slideshow-slide:not([hidden])')
    );
    const target = slides[index];
    if (!(target instanceof HTMLElement)) return;

    // Keep accessibility/current state in sync immediately.
    slides.forEach((slide, slideIndex) => {
      slide.setAttribute('aria-hidden', slideIndex === index ? 'false' : 'true');
    });

    if (slideshow && 'current' in slideshow) {
      slideshow.current = index;
    }

    // Force an instant horizontal jump even though Horizon's base CSS declares
    // scroll-behavior:smooth. Repeating once next frame makes this resilient to
    // a just-rendered gallery finishing its first layout pass.
    const previousScrollBehavior = scroller.style.scrollBehavior;
    scroller.style.scrollBehavior = 'auto';
    scroller.scrollLeft = target.offsetLeft;

    requestAnimationFrame(() => {
      if (!scroller.isConnected || !target.isConnected) return;
      scroller.scrollLeft = target.offsetLeft;
      scroller.style.scrollBehavior = previousScrollBehavior;
      this.#syncDaymeritThumbnails(index);
    });

    this.#syncDaymeritThumbnails(index);
  };

  #syncDaymeritThumbnails(forcedIndex) {
    const root = this.#daymeritGalleryRoot;
    if (!root) return;

    this.#patchDaymeritThumbnailScrolling();

    const slideshow = this.slideshow;
    const current = Number.isInteger(forcedIndex) ? forcedIndex : Number(slideshow?.current ?? 0);
    const buttons = Array.from(root.querySelectorAll('[data-dm-gallery-thumb]'));

    buttons.forEach((button, index) => {
      button.setAttribute('aria-current', index === current ? 'true' : 'false');
    });

    const active = buttons[current];
    if (active instanceof HTMLElement) {
      this.#scrollDaymeritThumbnail(active, { behavior: 'smooth' });
    }
  }

  zoom(index, event) {
    this.refs.zoomDialogComponent?.open(index, event);
  }

  preloadImage(index) {
    const zoomDialogMedia = this.refs.zoomDialogComponent?.refs.media[index];
    if (!zoomDialogMedia) return;
    this.refs.zoomDialogComponent?.loadHighResolutionImage(zoomDialogMedia);
  }

  get slideshow() {
    return this.refs.slideshow;
  }

  get media() {
    return this.refs.media;
  }

  get presentation() {
    return this.dataset.presentation;
  }
}

if (!customElements.get('media-gallery')) {
  customElements.define('media-gallery', MediaGallery);
}
